import { useEffect, useRef, useCallback } from 'react';
import { buildGeneralInteractionDedupeKey } from '../utils/strategemTargetPosition';

/**
 * Hook to detect strategem damage to general and trigger damage explosion animations
 * Uses the same damage animation system as unit attacks on general
 * @param {Object} player - Player object with health and generalInteractions
 * @param {Object} opponent - Opponent object with health and generalInteractions
 * @param {Function} setDamageAnimationTrigger - (triggerKey, trigger) => void; queues one damage FX + sound
 * @param {Function} getAnimatedStrategemInstanceIds - Legacy: when `isStrategemAnimatingToGeneral` is omitted, suppress if instanceId has any animation
 * @param {Function} [isStrategemAnimatingToGeneral] - (sourceInstanceId) => boolean; suppress duplicate FX only when the strategem projectile is aimed at the general (not e.g. Kamikaze 314 → friendly unit + separate general damage)
 * @returns {void}
 */
export const useStrategemGeneralDamage = (
  player,
  opponent,
  setDamageAnimationTrigger,
  getAnimatedStrategemInstanceIds = null,
  isStrategemAnimatingToGeneral = null
) => {
  const previousPlayerInteractionsRef = useRef(new Set());
  const previousOpponentInteractionsRef = useRef(new Set());
  const animationCounterRef = useRef(0);
  const hasLoggedInitRef = useRef(false);
  
  // Only log initialization once
  if (!hasLoggedInitRef.current && process.env.NODE_ENV === 'development') {
    console.log('🔍 useStrategemGeneralDamage: Hook initialized', {
      hasPlayer: !!player,
      hasOpponent: !!opponent,
      hasSetDamageAnimationTrigger: !!setDamageAnimationTrigger,
      playerInteractions: player?.generalInteractions?.length || 0,
      opponentInteractions: opponent?.generalInteractions?.length || 0
    });
    hasLoggedInitRef.current = true;
  }

  // Find general element by data-zone attribute (same as unit attack system)
  const findGeneralElement = useCallback((isOpponent) => {
    const zone = isOpponent ? 'opponent_general' : 'player_general';
    return document.querySelector(`[data-zone="${zone}"]`);
  }, []);

  // Trigger damage animation at general position (same system as unit attacks)
  const triggerGeneralDamageAnimation = useCallback((isOpponent, damageAmount) => {
    const generalElement = findGeneralElement(isOpponent);
    if (!generalElement) {
      console.warn(`⚠️ Strategem general damage: Could not find general element for ${isOpponent ? 'opponent' : 'player'}`);
      return;
    }

    const rect = generalElement.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    if (setDamageAnimationTrigger) {
      animationCounterRef.current += 1;
      const animationKey = `strategem-general-${isOpponent ? 'opponent' : 'player'}-${animationCounterRef.current}-${Date.now()}`;
      
      console.log(`💥 useStrategemGeneralDamage: Triggering damage animation at general position (${x}, ${y}) for ${isOpponent ? 'opponent' : 'player'}`);
      
      setDamageAnimationTrigger(animationKey, {
        attackId: animationKey,
        x: x,
        y: y,
        slotElement: generalElement,
        rect: rect,
        timestamp: Date.now(),
        isGeneralAnimation: true,
        damageAmount: damageAmount > 0 ? damageAmount : undefined,
      });
    }
  }, [findGeneralElement, setDamageAnimationTrigger]);

  // Track player general interactions for strategem damage
  useEffect(() => {
    if (!player?.generalInteractions) {
      return;
    }

    const currentInteractions = player.generalInteractions || [];
    const previousInteractions = previousPlayerInteractionsRef.current;

    // Find new strategem damage interactions
    currentInteractions.forEach((interaction) => {
      const interactionKey = buildGeneralInteractionDedupeKey(interaction);

      // Check if this is a new strategem damage interaction
      if (
        !previousInteractions.has(interactionKey) &&
        interaction.sourceType === 'strategem' &&
        interaction.effectType === 'damage' &&
        interaction.amount > 0
      ) {
        // Kamikaze (314): projectile targets a friendly unit; a separate general metadata animation may run — still show default general damage FX for the 5 to the general.
        // Venom Cloud (318): damage burst must use findGeneralElement on the victim only (BattleUI skips onDamageAtTarget). Never suppress — generalInteractions are authoritative.
        const suppressDuplicateFx =
          interaction.sourceId !== 314 &&
          interaction.sourceId !== 318 &&
          (typeof isStrategemAnimatingToGeneral === 'function'
            ? isStrategemAnimatingToGeneral(interaction.sourceInstanceId)
            : (getAnimatedStrategemInstanceIds ? getAnimatedStrategemInstanceIds().has(interaction.sourceInstanceId) : false));
        if (suppressDuplicateFx) {
          console.log(`⏭️ Skipping duplicate damage animation for player general - strategem ${interaction.sourceName} already has general-target animation`);
          previousInteractions.add(interactionKey);
          return;
        }
        
        console.log(`💥 Strategem damaged player general: ${interaction.sourceName} dealt ${interaction.amount} damage`);
        triggerGeneralDamageAnimation(false, interaction.amount);
        previousInteractions.add(interactionKey);
      }
    });

    // Update previous interactions set (keep only current ones)
    previousPlayerInteractionsRef.current = new Set(
      currentInteractions.map((interaction) => buildGeneralInteractionDedupeKey(interaction))
    );
  }, [player?.generalInteractions, triggerGeneralDamageAnimation, getAnimatedStrategemInstanceIds, isStrategemAnimatingToGeneral]);

  // Track opponent general interactions for strategem damage
  useEffect(() => {
    if (!opponent?.generalInteractions) {
      return;
    }

    const currentInteractions = opponent.generalInteractions || [];
    const previousInteractions = previousOpponentInteractionsRef.current;

    // Find new strategem damage interactions
    currentInteractions.forEach((interaction) => {
      const interactionKey = buildGeneralInteractionDedupeKey(interaction);

      // Check if this is a new strategem damage interaction
      if (
        !previousInteractions.has(interactionKey) &&
        interaction.sourceType === 'strategem' &&
        interaction.effectType === 'damage' &&
        interaction.amount > 0
      ) {
        const suppressDuplicateFx =
          interaction.sourceId !== 314 &&
          interaction.sourceId !== 318 &&
          (typeof isStrategemAnimatingToGeneral === 'function'
            ? isStrategemAnimatingToGeneral(interaction.sourceInstanceId)
            : (getAnimatedStrategemInstanceIds ? getAnimatedStrategemInstanceIds().has(interaction.sourceInstanceId) : false));
        if (suppressDuplicateFx) {
          console.log(`⏭️ Skipping duplicate damage animation for opponent general - strategem ${interaction.sourceName} already has general-target animation`);
          previousInteractions.add(interactionKey);
          return;
        }
        
        console.log(`💥 Strategem damaged opponent general: ${interaction.sourceName} dealt ${interaction.amount} damage`);
        triggerGeneralDamageAnimation(true, interaction.amount);
        previousInteractions.add(interactionKey);
      }
    });

    // Update previous interactions set (keep only current ones)
    previousOpponentInteractionsRef.current = new Set(
      currentInteractions.map((interaction) => buildGeneralInteractionDedupeKey(interaction))
    );
  }, [opponent?.generalInteractions, triggerGeneralDamageAnimation, getAnimatedStrategemInstanceIds, isStrategemAnimatingToGeneral]);
};

export default useStrategemGeneralDamage;

